import { useEffect, useRef, useState } from "react";
import { Send, Loader2, MessageCircle, Mic, Paperclip, Trash2, Square, X, ChevronDown, Save, ListPlus, FileText } from "lucide-react";
import { toast } from "sonner";
import { errMsg } from "@/lib/api";
import { LIB_CATS, personalize } from "@/lib/library";
import { fmtDateTime, waLink } from "@/lib/format";
import { useRecorder } from "@/components/chat/useRecorder";

export const waReplyText = (p, text) => `Olá, ${p.name.split(" ")[0]}! Aqui é a equipe da Igreja Unida Deus Proverá (ZAP IUDP).\n\n${text}`;

const field = "h-10 w-full rounded-md border border-zap-line bg-white px-2 text-sm";
const mmss = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

const useLibrary = (api) => {
  const [lib, setLib] = useState({ quick: [], audios: [], videos: [], links: [], numbers: [] });
  useEffect(() => {
    Promise.allSettled([api.get("/admin/quick-replies", { params: { active: true } }), api.get("/admin/audios"), api.get("/admin/videos"), api.get("/admin/links"), api.get("/admin/settings")])
      .then(([q, a, v, l, s]) => setLib({
        quick: q.value?.data || [], audios: a.value?.data || [], videos: (v.value?.data || []).filter((x) => x.status === "pronto"),
        links: l.value?.data || [], numbers: s.value?.data?.whatsapp_numbers || [],
      }));
  }, [api]);
  return lib;
};

const Extras = ({ lib, st, set, p }) => (
  <div data-testid="reply-extras" className="grid max-h-[40vh] grid-cols-2 gap-2 overflow-y-auto border-b border-zap-line bg-zap-mist/50 p-3 xl:grid-cols-3">
    <select data-testid="reply-library-select" value="" className={field}
      onChange={(e) => { const r = lib.quick.find((x) => x.id === e.target.value); if (r) { const t = personalize(r.text, p.name); set({ text: st.text.trim() ? `${st.text.trimEnd()}\n\n${t}` : t, audioId: r.audio_id || st.audioId }); } }}>
      <option value="">Resposta pronta…</option>
      {LIB_CATS.map(([k, l]) => { const items = lib.quick.filter((r) => r.category === k); return items.length ? <optgroup key={k} label={l}>{items.map((r) => <option key={r.id} value={r.id}>{r.title}</option>)}</optgroup> : null; })}
    </select>
    <select data-testid="reply-audio-select" value={st.audioId} onChange={(e) => set({ audioId: e.target.value })} className={field}>
      <option value="">Sem áudio oficial</option>
      {lib.audios.map((a) => <option key={a.id} value={a.id}>Áudio: {a.title}</option>)}
    </select>
    <select data-testid="reply-video-select" value={st.videoId} onChange={(e) => set({ videoId: e.target.value })} className={field}>
      <option value="">Sem vídeo</option>
      {lib.videos.map((v) => <option key={v.id} value={v.id}>Vídeo: {v.title}</option>)}
    </select>
    <input data-testid="reply-youtube" value={st.yt} onChange={(e) => set({ yt: e.target.value })} placeholder="Link do YouTube (opcional)" className={field} />
    {lib.links.length > 0 && (
      <select data-testid="reply-saved-link" value="" onChange={(e) => { const l = lib.links.find((x) => x.id === e.target.value); if (l) set({ link: l.url }); }} className={field}>
        <option value="">Link pronto…</option>
        {lib.links.map((l) => <option key={l.id} value={l.id}>{l.title}</option>)}
      </select>
    )}
    <input data-testid="reply-link" value={st.link} onChange={(e) => set({ link: e.target.value })} placeholder="Outro link (site, formulário…)" className={field} />
    {lib.numbers.length > 0 && (
      <select data-testid="reply-from-number" value={st.fromNumber || lib.numbers[0].label} onChange={(e) => set({ fromNumber: e.target.value })} className={field}>
        {lib.numbers.map((n) => <option key={n.label} value={n.label}>Enviar WhatsApp de: {n.label}</option>)}
      </select>
    )}
  </div>
);

const Chips = ({ lib, st, set }) => {
  const chips = [
    st.audioId && ["audioId", `Áudio: ${lib.audios.find((a) => a.id === st.audioId)?.title || ""}`],
    st.videoId && ["videoId", `Vídeo: ${lib.videos.find((v) => v.id === st.videoId)?.title || ""}`],
    st.yt.trim() && ["yt", "YouTube"], st.link.trim() && ["link", `Link: ${st.link}`],
  ].filter(Boolean);
  if (!chips.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5 px-3 pt-2">
      {chips.map(([k, l]) => (
        <span key={k} data-testid={`reply-chip-${k}`} className="flex max-w-[260px] items-center gap-1 rounded-full bg-zap-mist px-2.5 py-1 text-xs font-semibold text-zap-deep">
          <span className="truncate">{l}</span>
          <button type="button" data-testid={`reply-chip-remove-${k}`} onClick={() => set({ [k]: "" })} aria-label="Remover"><X className="h-3.5 w-3.5" /></button>
        </span>
      ))}
    </div>
  );
};

const RecordingBar = ({ recorder, onSend, busy }) => (
  <div data-testid="admin-recording-bar" className="flex items-center gap-3 p-3">
    <button type="button" data-testid="admin-recording-cancel" onClick={recorder.cancel} className="flex h-11 w-11 items-center justify-center rounded-full hover:bg-red-50" aria-label="Descartar gravação"><Trash2 className="h-5 w-5 text-red-700" /></button>
    <span className="flex flex-1 items-center gap-2 text-[15px] font-semibold text-zap-ink">
      <span className="h-3 w-3 animate-pulse rounded-full bg-red-600" />Gravando… <span data-testid="admin-recording-time" className="tabular-nums">{mmss(recorder.seconds)}</span>
    </span>
    <button type="button" data-testid="admin-recording-send" onClick={onSend} disabled={busy} className="flex h-11 items-center gap-2 rounded-full bg-zap-deep px-5 text-sm font-bold text-white disabled:opacity-50">
      <Square className="h-4 w-4 fill-white" />PARAR E ENVIAR
    </button>
  </div>
);

const EMPTY = { audioId: "", videoId: "", yt: "", link: "" };
const ACCEPT = "image/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt,audio/*,.ogg,.opus,.m4a,.mp3,.wav,.mpeg";
const MAX_MB = 16;

const Pending = ({ pending, onRemove }) => (
  <div data-testid="reply-attachment" className="mx-3 mt-2 flex items-center gap-3 rounded-lg border border-zap-line bg-zap-mist p-2">
    {pending.url ? <img src={pending.url} alt="Anexo" className="h-16 w-16 rounded-md object-cover" />
      : <span className="flex h-12 w-12 items-center justify-center rounded-md bg-white"><FileText className="h-6 w-6 text-zap-deep" /></span>}
    <div className="min-w-0 flex-1">
      <p data-testid="reply-attachment-name" className="truncate text-sm font-semibold">{pending.file.name}</p>
      <p className="text-xs text-muted-foreground">{pending.file.size < 1024 * 1024 ? `${Math.max(1, Math.round(pending.file.size / 1024))} KB` : `${(pending.file.size / 1024 / 1024).toFixed(1)} MB`} · vai junto ao tocar em Enviar</p>
    </div>
    <button type="button" data-testid="reply-attachment-remove" onClick={onRemove} className="flex h-9 w-9 items-center justify-center rounded-full hover:bg-white" aria-label="Remover anexo"><X className="h-4 w-4" /></button>
  </div>
);

export const ReplyBox = ({ api, p, onSent }) => {
  const lib = useLibrary(api);
  const [text, setText] = useState(p.reply_draft || "");
  const [st, setSt] = useState({ ...EMPTY, fromNumber: "" });
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const recorder = useRecorder();
  const ta = useRef(null);
  const fileInput = useRef(null);
  const [pending, setPending] = useState(null);
  const [drag, setDrag] = useState(false);
  const pick = (file) => {
    if (!file) return;
    if (file.type.startsWith("video/") && !/\.(mpeg|mpga|ogg|oga|opus|weba|m4a)$/i.test(file.name)) { toast.error("Para vídeos, cadastre em Biblioteca → Vídeos e escolha em “Prontos”."); return; }
    if (file.size > MAX_MB * 1024 * 1024) { toast.error(`Arquivo muito grande. Limite de ${MAX_MB} MB.`); return; }
    if (pending?.url) URL.revokeObjectURL(pending.url);
    setPending({ file, url: file.type.startsWith("image/") ? URL.createObjectURL(file) : null });
  };
  const clearPending = () => { if (pending?.url) URL.revokeObjectURL(pending.url); setPending(null); };
  const onPaste = (e) => {
    const item = [...(e.clipboardData?.items || [])].find((i) => i.kind === "file");
    const f = item?.getAsFile();
    if (!f) return;
    e.preventDefault();
    const ext = (f.type.split("/")[1] || "png").replace("jpeg", "jpg");
    pick(f.name && f.name !== "image.png" ? f : new File([f], `imagem-colada.${ext}`, { type: f.type }));
  };
  const set = (patch) => { if ("text" in patch) setText(patch.text); setSt((s) => ({ ...s, ...patch })); };
  useEffect(() => {
    const el = ta.current;
    if (el) { el.style.height = "auto"; el.style.height = `${Math.min(el.scrollHeight, window.innerWidth < 640 ? 120 : 200)}px`; }
  }, [text]);
  const hasAny = !!(pending || text.trim() || st.audioId || st.videoId || st.yt.trim() || st.link.trim());

  const saveDraft = async () => {
    try { await api.put(`/admin/participants/${p.id}/draft`, { text }); onSent(); toast.success(text.trim() ? "Rascunho salvo." : "Rascunho removido."); } catch (e) { toast.error(errMsg(e)); }
  };
  const send = async (channel) => {
    if (!hasAny) return;
    setBusy(true);
    const w = channel === "whatsapp" ? window.open("", "_blank") : null;
    try {
      if (pending && channel === "site") {
        const fd = new FormData();
        fd.append("file", pending.file); fd.append("text", text);
        await api.post(`/admin/participants/${p.id}/attach`, fd);
        clearPending();
        const extras = st.audioId || st.videoId || st.yt.trim() || st.link.trim();
        if (extras) await api.post(`/admin/participants/${p.id}/reply`, { text: "", channel, audio_id: st.audioId || null, video_id: st.videoId || null, youtube_url: st.yt || null, link_url: st.link || null });
        setText(""); setSt((s) => ({ ...s, ...EMPTY })); setOpen(false); onSent();
        toast.success("Enviado na conversa do ZAP IUDP.");
        setBusy(false);
        return;
      }
      const { data } = await api.post(`/admin/participants/${p.id}/reply`, {
        text, channel, audio_id: st.audioId || null, video_id: st.videoId || null, youtube_url: st.yt || null,
        link_url: st.link || null, from_number: st.fromNumber || lib.numbers[0]?.label || null,
      });
      setText(""); setSt((s) => ({ ...s, ...EMPTY })); setOpen(false); onSent();
      if (w) { w.location.href = waLink(p.whatsapp, waReplyText(p, data.text)); toast.info("WhatsApp aberto. Depois de enviar, marque ENVIO REALIZADO na mensagem."); }
      else toast.success("Resposta enviada na conversa do ZAP IUDP.");
    } catch (e) { if (w) w.close(); toast.error(errMsg(e)); }
    setBusy(false);
  };
  const startRec = async () => {
    try { await recorder.start(); } catch { toast.error("Não foi possível usar o microfone. Permita o acesso ao microfone no navegador."); }
  };
  const sendVoice = async () => {
    const blob = await recorder.stop();
    if (!blob || blob.size < 500) { toast.error("Gravação muito curta. Tente de novo."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", new File([blob], `audio.${blob.type.includes("mp4") ? "m4a" : blob.type.includes("ogg") ? "ogg" : "webm"}`, { type: blob.type }));
      await api.post(`/admin/participants/${p.id}/voice`, fd);
      onSent(); toast.success("Áudio enviado na conversa do ZAP IUDP.");
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  return (
    <div data-testid="reply-box" className={`shrink-0 border-t bg-white transition-colors ${drag ? "border-zap-green bg-zap-mist" : "border-zap-line"}`}
      onDragOver={(e) => { if (e.dataTransfer.types.includes("Files")) { e.preventDefault(); setDrag(true); } }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); pick(e.dataTransfer.files?.[0]); }}>
      {open && <Extras lib={lib} st={{ ...st, text }} set={set} p={p} />}
      <Chips lib={lib} st={st} set={set} />
      {pending && <Pending pending={pending} onRemove={clearPending} />}
      <input ref={fileInput} type="file" accept={ACCEPT} className="hidden" data-testid="reply-file-input"
        onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }} />
      {recorder.recording ? <RecordingBar recorder={recorder} onSend={sendVoice} busy={busy} /> : (
        <div className="flex items-end gap-2 px-3 pb-2 pt-3">
          <button type="button" data-testid="reply-extras-toggle" onClick={() => setOpen(!open)} aria-expanded={open} title="Respostas prontas, áudios oficiais, vídeos e links"
            className={`flex h-11 shrink-0 items-center gap-1 rounded-full border px-3 text-sm font-semibold transition-colors ${open ? "border-zap-deep bg-zap-deep text-white" : "border-zap-line text-zap-deep hover:bg-zap-mist"}`}>
            <ListPlus className="h-4 w-4" /><span className="hidden sm:inline">Prontos</span><ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
          <button type="button" data-testid="reply-attach-btn" onClick={() => fileInput.current?.click()} disabled={busy} aria-label="Anexar foto ou arquivo" title="Anexar foto ou arquivo da galeria"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-zap-line text-zap-deep transition-colors hover:bg-zap-mist disabled:opacity-50">
            <Paperclip className="h-5 w-5" />
          </button>
          <textarea ref={ta} data-testid="admin-reply-input" value={text} onChange={(e) => setText(e.target.value)} onPaste={onPaste} rows={1}
            placeholder={pending ? "Legenda (opcional)…" : "Escreva ou cole uma imagem…"}
            className="min-h-11 flex-1 resize-none rounded-2xl border border-zap-line bg-zap-mist px-4 py-2.5 text-[15px] leading-6 outline-none focus:border-zap-green" />
          <button type="button" data-testid="admin-record-btn" onClick={startRec} disabled={busy} aria-label="Gravar áudio agora" title="Gravar áudio agora"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-zap-deep text-white transition-transform hover:scale-105 disabled:opacity-50">
            <Mic className="h-5 w-5" />
          </button>
        </div>
      )}
      {!recorder.recording && (
        <div className="grid grid-cols-[1fr_1fr_auto] items-center gap-2 px-3 pb-3 sm:flex sm:flex-wrap">
          <button data-testid="admin-reply-send" onClick={() => send("site")} disabled={busy || !hasAny} className="flex h-11 items-center justify-center gap-2 rounded-lg bg-zap-deep px-3 text-sm font-bold text-white disabled:opacity-40 sm:px-4">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} <span className="sm:hidden">ENVIAR</span><span className="hidden sm:inline">RESPONDER NO ZAP IUDP</span>
          </button>
          <button data-testid="admin-reply-whatsapp" onClick={() => send("whatsapp")} disabled={busy || !!pending || (!text.trim() && !st.link.trim() && !st.yt.trim())} title={pending ? "Anexos vão só pelo ZAP IUDP" : undefined} className="flex h-11 items-center justify-center gap-2 rounded-lg border border-zap-deep px-3 text-sm font-bold text-zap-deep disabled:opacity-40 sm:px-4">
            <MessageCircle className="h-4 w-4" /> <span className="sm:hidden">WHATSAPP</span><span className="hidden sm:inline">PELO WHATSAPP</span>
          </button>
          <button data-testid="admin-reply-draft" onClick={saveDraft} disabled={busy || text.trim() === (p.reply_draft || "").trim()} aria-label="Salvar rascunho" title="Salvar rascunho"
            className="flex h-11 items-center justify-center gap-1 rounded-lg px-2 text-sm font-medium text-zap-ink/70 hover:bg-zap-mist disabled:opacity-40 sm:px-3"><Save className="h-4 w-4 sm:hidden" /><span className="hidden sm:inline">Salvar rascunho</span></button>
          {p.reply_draft_at && <span className="col-span-3 text-xs text-muted-foreground">Rascunho de {fmtDateTime(p.reply_draft_at)}</span>}
        </div>
      )}
    </div>
  );
};
